// --------------------------------------------------------------
// Created On: 2026-10-25
// Author: Zachary Thomas
//
// Last Modified: 2026-11-02
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import { useState, useEffect } from "react";
import GetAssetOperationResponseBody from "src/types/api/GetAssetOperationResponseBody";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import deepCopy from "src/utilities/deepCopy";
import useApi from "src/hooks/useApi";
import useReduxSelector from "src/hooks/useReduxSelector";
import useLastActive from "src/hooks/useLastActive";
import nowToUtcIso from "src/utilities/time/nowToUtcIso";
import {
  API,
  MS_PER_SECOND,
  DEVICES_THAT_REPORT_READY_FOR_COMMANDS,
  MAX_QUERIES_AFTER_REMOTE_OPERATION,
  SECONDS_BETWEEN_OPERATION_READ_QUERIES,
  GATEWAY_TIMEOUT_STATUS,
  DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS,
} from "src/constants/miscellaneous";
import { ASSET_DETAILS_POLLING_DELAY_SECONDS, ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS } from "src/constants/polling";
import { LOCAL_STORAGE_DISABLE_DASHBOARD_INACTIVE_CHECK_KEY } from "src/constants/localStorageVariables";

// Hook that polls an asset's remote operation data and sends remote operation commands.
export default function useRemoteOperation(assetId: number): RemoteOperation {
  const BLANK_MESSAGE_MAP: MessageMap = { error: "", warning: "", success: "" };
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [assetData, setAssetData] = useState<AssetOperationData | null>(null);
  const [isLockedOut, setIsLockedOut] = useState<boolean>(false);
  const [attributeReadings, setAttributeReadings] = useState<AttributeReadings>({
    listAttributes: [],
    gaugeAttributes: [],
    digitalOutputs: [],
  });
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [deviceIsReadyForCommand, setDeviceIsReadyForCommand] = useState<boolean | null>(null);
  const [deviceReadyCheckedUtc, setDeviceReadyCheckedUtc] = useState<string | null>(null);
  const [messageMap, setMessageMap] = useState<MessageMap>(BLANK_MESSAGE_MAP);
  const [queriesRemaining, setQueriesRemaining] = useState<number>(0);
  const [dataPollCounter, setDataPollCounter] = useState<number>(0);
  const [timerCounter, setTimerCounter] = useState<number>(0);
  const userInactiveSeconds = useLastActive(ASSET_DETAILS_POLLING_DELAY_SECONDS);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const userCanOperate =
    assetData !== null &&
    DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(assetData.deviceType || "") &&
    assetData.operationIsAllowed;

  // Get the asset's operation details, operation logs, and the current relevant attribute readings.
  useApi(
    () => {
      if (dataPollCounter === 0) {
        setInitialLoading(true);
      }
      return true;
    },
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}/operation`,
    },
    async (response: Response, responseBody: GetAssetOperationResponseBody) => {
      if (response.ok && responseBody) {
        setAssetData({
          assetName: responseBody.assetName,
          assetNickname: responseBody.assetNickname ?? "",
          operationIsAllowed: responseBody.operationIsAllowed,
          controllerModel: responseBody.controllerModel,
          controllerApplicationType: responseBody.controllerApplicationType,
          deviceId: responseBody.deviceId,
          deviceIdentifier: responseBody.deviceIdentifier,
          deviceType: responseBody.deviceType,
          isDeviceAttached: responseBody.isDeviceAttached,
          canUnlock: responseBody.canUnlock,
          isMigrating: responseBody.isMigrating,
          isRented: responseBody.isRented,
          lastConfigAttemptUtc: responseBody.lastConfigAttemptUtc,
          lastUpdateFirmwareAttemptUtc: responseBody.lastUpdateFirmwareAttemptUtc,
          lastComm: responseBody.lastComm,
          configurationStatus: responseBody.configurationStatus,
          firmwareStatus: responseBody.firmwareStatus,
          onLatestFirmware: responseBody.onLatestFirmware,
          remoteOperationSupported:
            responseBody.remoteOperationSupported &&
            DEVICES_THAT_SUPPORT_REMOTE_OPERATIONS.includes(responseBody.deviceType || ""),
        });
        setIsLockedOut(responseBody.isLockedOut);
        setAttributeReadings({
          listAttributes: responseBody.listAttributes,
          gaugeAttributes: responseBody.gaugeAttributes,
          digitalOutputs: responseBody.digitalOutputs,
        });
        setLogs(responseBody.assetOperationLogs);
        setFailedToLoad(false);
      } else {
        console.error("Internal server error. Unable to retrieve operational data.");
        setFailedToLoad(true);
      }
      setInitialLoading(false);
    },
    [assetId, dataPollCounter]
  );

  // Whenever data is polled, check if the device is free to accept a new command.
  // Inactivity is ALWAYS consider for this check, since it sometimes has to send commands to a device.
  useApi(
    () =>
      DEVICES_THAT_REPORT_READY_FOR_COMMANDS.includes(assetData?.deviceType || "") &&
      userInactiveSeconds < ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS,
    {
      method: "GET",
      url: `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/pendingcommand`,
    },
    async (response: Response, responseBody: PendingCommandResponseBody) => {
      if (response.ok && responseBody) {
        setDeviceIsReadyForCommand(!responseBody.pendingCommand);
      } else {
        setDeviceIsReadyForCommand(false);
      }
      setDeviceReadyCheckedUtc(nowToUtcIso(0));
    },
    [dataPollCounter, assetData?.deviceType]
  );

  // Periodically poll data to give users a close to live feed (does not actually sample, unlike a real time read).
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setTimerCounter((prev) => prev + 1);
      // Only poll data if the user is active.
      const inactiveCheckDisabled = localStorage.getItem(LOCAL_STORAGE_DISABLE_DASHBOARD_INACTIVE_CHECK_KEY) === "true";
      if (inactiveCheckDisabled) {
        console.log("Looking to see if data should be polled. Inactivity is not considered due to user settings.");
      } else {
        console.log(
          `Looking to see if data should be polled` +
            ` (inactive for ${userInactiveSeconds} seconds of ${ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS} second limit).`
        );
      }
      if (inactiveCheckDisabled || userInactiveSeconds < ASSET_DETAILS_MAX_INACTIVE_USER_SECONDS) {
        setDataPollCounter((prev) => prev + 1);
      } else {
        console.log("User is inactive. Data will not be polled.");
      }
    }, ASSET_DETAILS_POLLING_DELAY_SECONDS * MS_PER_SECOND);
    return () => {
      clearTimeout(newTimerId);
    };
  }, [timerCounter]);

  // Temporarily poll displayed data after a remote operation to take as close to live readings as possible.
  useEffect(() => {
    if (queriesRemaining > 0) {
      const timerId = setTimeout(
        () => sampleDevice(assetId, queriesRemaining),
        MS_PER_SECOND * SECONDS_BETWEEN_OPERATION_READ_QUERIES
      );
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [queriesRemaining, assetId]);

  // Sample device to get the most current device data.
  async function sampleDevice(assetId: number, queriesRemaining: number): Promise<void> {
    if (assetId !== 0 && queriesRemaining > 0) {
      const [response, responseBody] = (await apiRequest(
        `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/live`,
        "GET",
        null
      )) as [Response, GetAttributesResponseBody];

      if (response.ok) {
        // Since we got new values for existing attributes, update the existing attributes.
        const attributeReadingsDeepCopy = deepCopy(attributeReadings);
        let attributeChanged = false;

        responseBody.attributes.forEach((updatedAttribute) => {
          const foundAttribute = [
            ...attributeReadingsDeepCopy.listAttributes,
            ...attributeReadingsDeepCopy.gaugeAttributes,
          ].find((attribute) => updatedAttribute.regAttributeId === attribute.regAttributeId);

          if (
            foundAttribute !== undefined &&
            updatedAttribute.currentValueUtc !== null &&
            foundAttribute.currentValueUtc !== null &&
            updatedAttribute.currentValueUtc > foundAttribute.currentValueUtc
          ) {
            foundAttribute.currentValue = updatedAttribute.currentValue;
            foundAttribute.currentValueUtc = updatedAttribute.currentValueUtc;
            attributeChanged = true;
          }
        });
        if (attributeChanged) {
          setAttributeReadings(attributeReadingsDeepCopy);
        }
      }
      // Decrement the number of remaining queries regardless of if the sample was successful or not.
      setQueriesRemaining(queriesRemaining - 1);
    }
  }

  // Sends a remote operation command and reports the result to the user.
  async function sendCommand(command: Command): Promise<void> {
    setMessageMap(BLANK_MESSAGE_MAP);
    setQueriesRemaining(0);
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/${command.path}`,
      "POST",
      command.body
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setMessageMap({ ...BLANK_MESSAGE_MAP, success: command.successMessage });
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setMessageMap({
        ...BLANK_MESSAGE_MAP,
        warning: "Request timeout. The command's status could not be confirmed. Please try again.",
      });
    } else {
      setMessageMap({ ...BLANK_MESSAGE_MAP, error: await getApiError(response, command.errorMessage) });
    }

    // Get the most recent operation logs since we just performed an operation.
    void refreshLogs();

    // Perform live reads for a bit after this operation.
    if (command.liveReads) {
      setQueriesRemaining(MAX_QUERIES_AFTER_REMOTE_OPERATION);
    }
  }

  // Change communication mode.
  async function changeCommunicationMode(command: string): Promise<void> {
    await sendCommand({
      path: "operation/commsmode",
      body: { command: command },
      successMessage: "Communication mode command sent successfully.",
      errorMessage: "Failed while attempting to change communication mode.",
      liveReads: false,
    });
  }

  // Change running status.
  async function changeRunningStatus(command: string): Promise<void> {
    await sendCommand({
      path: "operation/runningstatus",
      body: { command: command },
      successMessage: "Running status command sent successfully.",
      errorMessage: "Failed while attempting to change running status.",
      liveReads: true,
    });
  }

  // Change speed.
  async function changeSpeed(command: number): Promise<void> {
    await sendCommand({
      path: "operation/speed",
      body: { command: command },
      successMessage: "Change speed command sent successfully.",
      errorMessage: "Failed while attempting to change speed.",
      liveReads: true,
    });
  }

  // Change digital output status based on the specified position.
  async function changeDigitalOutput(position: number, state: boolean): Promise<void> {
    await sendCommand({
      path: "operation/digital-output",
      body: { position: position, state: state },
      successMessage: "Digital output command sent successfully.",
      errorMessage: "Failed while attempting to change digital output status.",
      liveReads: true,
    });
  }

  // Changes a valve's position by a certain percent.
  async function changeValvePosition(valveNumber: number, percentOpen: number): Promise<void> {
    await sendCommand({
      path: "operation/open-valve",
      body: { valveNumber: valveNumber, percentOpen: percentOpen },
      successMessage: "Valve position command sent successfully.",
      errorMessage: "Failed while attempting to change valve position.",
      liveReads: true,
    });
  }

  // Clear failed commands from monitoring device.
  async function clearFailedCommands(): Promise<void> {
    await sendCommand({
      path: "operation/clearalerts",
      body: null,
      successMessage: "Clear device command sent successfully.",
      errorMessage: "Failed while attempting to clear pending commands.",
      liveReads: false,
    });
  }

  // Restart monitoring device.
  async function restartMonitoringDevice(): Promise<void> {
    await sendCommand({
      path: "operation/reset",
      body: null,
      successMessage: "Restart monitoring device command sent successfully.",
      errorMessage: "Failed while attempting to restart monitoring device.",
      liveReads: false,
    });
  }

  // Reapplies a device's configuration.
  async function reapplyConfiguration(): Promise<void> {
    await sendCommand({
      path: "reapply-config",
      body: null,
      successMessage: "Reapply configuration command sent successfully.",
      errorMessage: "Failed while attempting to reapply configuration.",
      liveReads: false,
    });
  }

  // Blink LEDs on monitoring device.
  async function blinkLeds(pattern: string, position: string): Promise<void> {
    await sendCommand({
      path: "operation/blink-led",
      body: { ledPattern: pattern, ledPosition: position },
      successMessage: "Blink LEDs command sent successfully.",
      errorMessage: "Failed while attempting to blink LEDs on the monitoring device.",
      liveReads: false,
    });
  }

  // Get the operation logs for the current asset and save them to state.
  async function refreshLogs(): Promise<void> {
    const [response, responseBody] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operatelog`,
      "GET",
      null
    )) as [Response, GetLogsResponseBody];
    if (response.ok) {
      setLogs(responseBody.assetOperationLogs);
    }
  }

  // Apply or remove the remote operation lock on the asset.
  async function updateLockStatus(isLocked: boolean): Promise<void> {
    setMessageMap((prev) => ({ ...prev, success: "" }));
    setLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${currentUser.companyId}/asset/${assetId}/operation/lockout`,
      "PUT",
      { lockout: isLocked }
    )) as [Response, PostResponseBody];
    setLoading(false);

    if (response.ok) {
      setIsLockedOut(isLocked);
    } else {
      const error = await getApiError(response, "Failed while attempting to change lock for remote operations.");
      setMessageMap((prev) => ({ ...prev, error: error }));
    }
  }

  // Replace the current error message.
  function setErrorMessage(error: string): void {
    setMessageMap((prev) => ({ ...prev, error: error }));
  }

  return {
    assetData: assetData,
    isLockedOut: isLockedOut,
    attributeReadings: attributeReadings,
    logs: logs,
    messageMap: messageMap,
    loading: loading || initialLoading,
    failedToLoad: failedToLoad,
    isSamplingLiveData: queriesRemaining > 0,
    deviceIsReadyForCommand: deviceIsReadyForCommand,
    deviceReadyCheckedUtc: deviceReadyCheckedUtc,
    userCanOperate: userCanOperate,
    actions: {
      setErrorMessage: setErrorMessage,
      refreshLogs: refreshLogs,
      changeCommunicationMode: changeCommunicationMode,
      changeRunningStatus: changeRunningStatus,
      changeSpeed: changeSpeed,
      changeDigitalOutput: changeDigitalOutput,
      changeValvePosition: changeValvePosition,
      clearFailedCommands: clearFailedCommands,
      restartMonitoringDevice: restartMonitoringDevice,
      reapplyConfiguration: reapplyConfiguration,
      blinkLeds: blinkLeds,
      updateLockStatus: updateLockStatus,
    },
  };
}

export interface RemoteOperation {
  assetData: AssetOperationData | null;
  isLockedOut: boolean;
  attributeReadings: AttributeReadings;
  logs: OperationLog[];
  messageMap: MessageMap;
  loading: boolean;
  failedToLoad: boolean;
  isSamplingLiveData: boolean;
  deviceIsReadyForCommand: boolean | null;
  deviceReadyCheckedUtc: string | null;
  userCanOperate: boolean;
  actions: RemoteOperationActions;
}

export interface AssetOperationData {
  assetName: string;
  assetNickname: string;
  operationIsAllowed: boolean;
  controllerModel: string | null;
  controllerApplicationType: string;
  deviceId: number | null;
  deviceIdentifier: string | null;
  deviceType: string | null;
  isDeviceAttached: boolean;
  canUnlock: boolean;
  isMigrating: boolean;
  isRented: boolean;
  lastConfigAttemptUtc: string | null;
  lastUpdateFirmwareAttemptUtc: string | null;
  lastComm: string | null;
  configurationStatus: Status | null;
  firmwareStatus: Status | null;
  onLatestFirmware: boolean | null;
  remoteOperationSupported: boolean;
}

export interface AttributeReadings {
  listAttributes: ListAttribute[];
  gaugeAttributes: GaugeAttribute[];
  digitalOutputs: DigitalOutput[];
}

export interface MessageMap {
  error: string;
  warning: string;
  success: string;
}

export interface RemoteOperationActions {
  setErrorMessage: (error: string) => void;
  refreshLogs: () => Promise<void>;
  changeCommunicationMode: (command: string) => Promise<void>;
  changeRunningStatus: (command: string) => Promise<void>;
  changeSpeed: (command: number) => Promise<void>;
  changeDigitalOutput: (position: number, state: boolean) => Promise<void>;
  changeValvePosition: (valveNumber: number, percentOpen: number) => Promise<void>;
  clearFailedCommands: () => Promise<void>;
  restartMonitoringDevice: () => Promise<void>;
  reapplyConfiguration: () => Promise<void>;
  blinkLeds: (pattern: string, position: string) => Promise<void>;
  updateLockStatus: (isLocked: boolean) => Promise<void>;
}

export interface OperationLog {
  assetOperationLogId: number;
  commandType: string;
  value: string;
  commandSucceeded: boolean;
  username: string;
  emailAddress: string;
  operationUtc: string;
}

export interface ListAttribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  unitShortName: string;
  currentValue: string | null;
  currentValueUtc: string | null;
}

export interface GaugeAttribute {
  regAttributeId: number;
  attributeCode: string;
  attributeName: string;
  unitShortName: string;
  currentValue: string | null;
  currentValueUtc: string | null;
  minValue: number;
  maxValue: number;
}

export interface DigitalOutput {
  position: number;
  operationLabel: string;
  trueLabel: string;
  falseLabel: string;
  currentValue: boolean | null;
  currentValueUtc: string | null;
}

export type Status = "IN_PROGRESS" | "SUCCEEDED" | "FAILED";

interface Command {
  path: string;
  body: object | null;
  successMessage: string;
  errorMessage: string;
  liveReads: boolean;
}

interface PostResponseBody {
  message: string;
}

interface PendingCommandResponseBody {
  pendingCommand: boolean;
}

interface GetLogsResponseBody {
  assetOperationLogs: OperationLog[];
}

interface GetAttributesResponseBody {
  attributes: ListAttribute[];
}
